import { CartProductOptionType } from '@/containers/types/CartProductOption'
import { CartShippingGroupType } from '@/containers/types/CartShippingGroup'
import { deepcopy } from '@/utils/utilData'

// key: skuCode
export type SkuCodeQuantityType = {
  [index: string]: { stockQuantity: number; purchaseQuantity: number }
}

export type SkuCodeQuantityTypeUtilType = {
  clear: (skuCodeQuantity: SkuCodeQuantityType) => void
  set: (target: SkuCodeQuantityType, source: SkuCodeQuantityType) => void
  addSkuCodeQuantity: (skuCodeQuantity: SkuCodeQuantityType, cartProductOption: CartProductOptionType) => void
  makeSkuCodeQuantityByCartShippingGroups: (cartShippingGroups: CartShippingGroupType[]) => SkuCodeQuantityType
  makeSkuCodeQuantityByCartProductOptions: (cartProductOptions: CartProductOptionType[]) => SkuCodeQuantityType
  getAvailablePurchaseStockQuantity: (
    skuCode: string,
    skuCodeQuantity: SkuCodeQuantityType,
    skuCodeQuantityCandidate?: SkuCodeQuantityType,
    extendPurchaseQuantity?: number
  ) => number | undefined
  isValidPurchaseQuantity: (
    skuCodeQuantity: SkuCodeQuantityType,
    cartProductOption: CartProductOptionType,
    skuCodeQuantityCandidate?: SkuCodeQuantityType
  ) => boolean
  isSkuCodeContains: (skuCodeQuantity: SkuCodeQuantityType, cartProductOption: CartProductOptionType) => boolean
}

const clear = (skuCodeQuantity: SkuCodeQuantityType) => {
  Object.keys(skuCodeQuantity).forEach((key) => {
    delete skuCodeQuantity[key]
  })
}

const set = (target: SkuCodeQuantityType, source: SkuCodeQuantityType) => {
  clear(target)
  const _source: SkuCodeQuantityType = deepcopy(source)
  if (_source) {
    Object.assign(target, _source)
  }
}

const addSkuCodeQuantity = (skuCodeQuantity: SkuCodeQuantityType, cartProductOption: CartProductOptionType) => {
  const purchaseQuantity = cartProductOption.purchaseQuantity
  cartProductOption.productOptionSkus?.forEach((productOptionSku) => {
    const code = productOptionSku.sku.code
    const count = productOptionSku.count
    if (code in skuCodeQuantity) {
      skuCodeQuantity[code].purchaseQuantity += purchaseQuantity * count
    } else {
      skuCodeQuantity[code] = {
        stockQuantity: productOptionSku.sku.stockQuantity,
        purchaseQuantity: purchaseQuantity * count,
      }
    }
  })
}

const makeSkuCodeQuantityByCartShippingGroups = (cartShippingGroups: CartShippingGroupType[]): SkuCodeQuantityType => {
  const skuCodeQuantity: SkuCodeQuantityType = {}
  cartShippingGroups.forEach((cartShippingGroup) => {
    cartShippingGroup.cartProducts.forEach((cartProduct) => {
      cartProduct.cartProductOptions.forEach((cartProductOption) => {
        addSkuCodeQuantity(skuCodeQuantity, cartProductOption)
      })
    })
  })
  return skuCodeQuantity
}

const makeSkuCodeQuantityByCartProductOptions = (cartProductOptions: CartProductOptionType[]): SkuCodeQuantityType => {
  const skuCodeQuantity: SkuCodeQuantityType = {}
  cartProductOptions.forEach((cartProductOption) => {
    addSkuCodeQuantity(skuCodeQuantity, cartProductOption)
  })
  return skuCodeQuantity
}

const getAvailablePurchaseStockQuantity = (
  skuCode: string,
  skuCodeQuantity: SkuCodeQuantityType,
  skuCodeQuantityCandidate?: SkuCodeQuantityType,
  extendPurchaseQuantity: number = 0
): number | undefined => {
  if (skuCode in skuCodeQuantity) {
    const { stockQuantity, purchaseQuantity } = skuCodeQuantity[skuCode]
    const purchaseQuantityCandidate =
      skuCodeQuantityCandidate && skuCode in skuCodeQuantityCandidate
        ? skuCodeQuantityCandidate[skuCode].purchaseQuantity
        : 0
    return stockQuantity - (purchaseQuantity + purchaseQuantityCandidate + extendPurchaseQuantity)
  } else if (skuCodeQuantityCandidate && skuCode in skuCodeQuantityCandidate) {
    const { stockQuantity, purchaseQuantity } = skuCodeQuantityCandidate[skuCode]
    return stockQuantity - (purchaseQuantity + extendPurchaseQuantity)
  }
  return undefined
}

const isValidPurchaseQuantity = (
  skuCodeQuantity: SkuCodeQuantityType,
  cartProductOption: CartProductOptionType,
  skuCodeQuantityCandidate?: SkuCodeQuantityType
) => {
  for (const productOptionSku of cartProductOption.productOptionSkus) {
    const availablePurchaseStockQuantity = SkuCodeQuantityTypeUtil.getAvailablePurchaseStockQuantity(
      productOptionSku.sku.code,
      skuCodeQuantity,
      skuCodeQuantityCandidate
    )
    if (!!availablePurchaseStockQuantity && availablePurchaseStockQuantity < 0) {
      return false
    }
  }
  return true
}

const isSkuCodeContains = (skuCodeQuantity: SkuCodeQuantityType, cartProductOption: CartProductOptionType): boolean => {
  if (cartProductOption.productOptionSkus) {
    for (const productOptionSku of cartProductOption.productOptionSkus) {
      if (productOptionSku.sku.code in skuCodeQuantity) {
        return true
      }
    }
  }
  return false
}

export const SkuCodeQuantityTypeUtil: SkuCodeQuantityTypeUtilType = {
  clear,
  set,
  addSkuCodeQuantity,
  makeSkuCodeQuantityByCartShippingGroups,
  makeSkuCodeQuantityByCartProductOptions,
  getAvailablePurchaseStockQuantity,
  isValidPurchaseQuantity,
  isSkuCodeContains,
}
